import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";
import { waLink, scrollToId } from "../constants/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=85&w=2000&auto=format&fit=crop";

const LINES = [
  { text: "Journeys that", accent: false },
  { text: "begin with", accent: false },
  { text: "trust.", accent: true },
];

const STATS = [
  { value: "12+", label: "Years of craft" },
  { value: "1000+", label: "Visas approved" },
  { value: "50+", label: "Destinations" },
];

export const HeroSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Dubai skyline glowing at night" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-black/40" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-16 pt-44 md:px-10">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          data-testid="hero-overline"
          className="mb-8 flex items-center gap-4 text-[11px] uppercase tracking-[0.4em] text-white/60"
        >
          <span className="h-px w-12 bg-sunset" />
          Gautam Travel · Est. 2014
        </motion.p>

        <h1 className="font-display text-5xl leading-[0.98] tracking-tight sm:text-7xl lg:text-8xl">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.25 + i * 0.16, ease: [0.16, 1, 0.3, 1] }}
                className={`block ${line.accent ? "italic text-sunset" : ""}`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.8 }}
          className="mt-8 max-w-xl text-base font-light text-white/70 md:text-lg"
        >
          International holidays, domestic escapes and visas that actually get approved — planned end
          to end by a team that has stamped 1000+ passports since 2014.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#destinations"
            onClick={(e) => scrollToId(e, "#destinations")}
            data-testid="hero-explore-btn"
            className="group inline-flex items-center gap-3 rounded-full bg-sunset px-8 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-coral"
          >
            Explore Packages
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href={waLink()}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-whatsapp-btn"
            className="inline-flex items-center gap-3 rounded-full border border-white/25 px-8 py-4 text-sm font-medium uppercase tracking-[0.2em] text-white/85 backdrop-blur transition-colors duration-300 hover:border-teal hover:text-teal"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp Us
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.35, duration: 1 }}
          data-testid="hero-stats"
          className="mt-16 grid max-w-2xl grid-cols-3 gap-6 border-t border-white/15 pt-8"
        >
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="font-display text-3xl text-white md:text-4xl">{s.value}</p>
              <p className="mt-1 text-[11px] uppercase tracking-[0.25em] text-white/50">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <div className="absolute right-8 top-1/2 hidden -translate-y-1/2 rotate-90 lg:block">
        <span className="text-[10px] uppercase tracking-[0.6em] text-white/35">gautamtravel.site</span>
      </div>
    </section>
  );
};
